import auth0 from 'auth0-js';
import { jwtDecode } from 'jwt-decode';
import { storeTokenInLocalStorage } from 'utils/helperMethods';
import { isDebugFlagEnabled } from 'utils/debugFlags';

// Seconds before a token's real expiry at which we consider it "stale" and
// proactively renew. Kept just under a short (5-10 min) access-token lifetime so
// a fresh token is always in place before the current one expires.
// TODO: Set this to 60 seconds for production
const REFRESH_SKEW_SECONDS = Number(process.env.REACT_APP_ACCESS_TOKEN_REFRESH_SKEW_SECONDS) || 60;

// Never schedule a renewal timer sooner than this (guards against a busy loop
// when a token is already at/near expiry).
const MIN_RENEWAL_DELAY_MS = 5000;

// Single shared auth0-js instance so login, callback, silent renewal and the
// request interceptors all operate on the same client/session.
export const webAuth = new auth0.WebAuth({
    domain: process.env.REACT_APP_AUTH0_DOMAIN_URL ?? '',
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID ?? '',
    audience: process.env.REACT_APP_AUTH0_AUDIENCE ?? '',
    scope: 'openid profile email',
    responseType: 'token id_token',
    redirectUri: `${window.location.origin}/callback`,
});

// De-dupes concurrent renewals: while a checkSession is in flight every caller
// awaits the same promise instead of spawning multiple silent-auth iframes.
let inFlightRenewal: Promise<string> | null = null;
let renewalTimer: ReturnType<typeof setTimeout> | null = null;

function getTokenExpiry(token: string | null): number | null {
    if (!token) return null;
    try {
        const decoded = jwtDecode<{ exp?: number }>(token);
        return decoded?.exp ?? null;
    } catch {
        return null;
    }
}

// Seconds remaining until the token expires. Returns a negative number for
// missing/undecodable/expired tokens so callers treat them as needing renewal.
function secondsUntilExpiry(token: string | null): number {
    const exp = getTokenExpiry(token);
    if (!exp) return -1;
    return exp - Date.now() / 1000;
}

// Wraps auth0-js checkSession (silent auth via the Auth0 session cookie) in a
// promise and persists the resulting token using the existing storage keys.
function renewViaCheckSession(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        webAuth.checkSession({}, (err, authResult) => {
            if (err) {
                reject(err);
                return;
            }
            if (authResult && authResult.accessToken && authResult.idToken) {
                storeTokenInLocalStorage(authResult);
                localStorage.setItem(
                    'email',
                    authResult.idTokenPayload?.email || localStorage.getItem('email') || '',
                );
                resolve(authResult.accessToken);
            } else {
                reject(new Error('Silent token renewal returned no token'));
            }
        });
    });
}

// Forces a silent renewal now (single-flight) and reschedules the next
// proactive renewal on success.
export function renewToken(): Promise<string> {
    if (inFlightRenewal) return inFlightRenewal;
    inFlightRenewal = renewViaCheckSession()
        .then((token) => {
            scheduleProactiveRenewal();
            return token;
        })
        .finally(() => {
            inFlightRenewal = null;
        });
    return inFlightRenewal;
}

// Returns a valid access token for the current request. Uses the cached token
// when it still has comfortable life left, otherwise renews before returning.
// On renewal failure it falls back to the existing token so the request still
// goes out and the existing 401 handling can redirect to login.
export async function ensureFreshToken(): Promise<string> {
    const current = localStorage.getItem('accessToken') || '';
    if (isDebugFlagEnabled('tokenExpiry')) {
        // @code-guard: skip-next-line reason="opt-in debug log gated behind debug.tokenExpiry (see utils/debugFlags.ts)"
        console.log('time left to expire', secondsUntilExpiry(current) - REFRESH_SKEW_SECONDS);
    }
    if (secondsUntilExpiry(current) > REFRESH_SKEW_SECONDS) {
        return current;
    }
    try {
        return await renewToken();
    } catch (error) {
        console.error('Token renewal failed; proceeding with existing token', error);
        return current;
    }
}

// Schedules a single precise renewal ~REFRESH_SKEW_SECONDS before the current
// token's real expiry (works for any token lifetime). Reschedules itself after
// each successful renewal.
export function scheduleProactiveRenewal(): void {
    if (renewalTimer) clearTimeout(renewalTimer);
    const remaining = secondsUntilExpiry(localStorage.getItem('accessToken'));
    const fireInMs = Math.max((remaining - REFRESH_SKEW_SECONDS) * 1000, MIN_RENEWAL_DELAY_MS);
    renewalTimer = setTimeout(() => {
        renewToken().catch((error) => {
            console.error('Proactive token renewal failed', error);
        });
    }, fireInMs);
}

export function stopProactiveRenewal(): void {
    if (renewalTimer) {
        clearTimeout(renewalTimer);
        renewalTimer = null;
    }
}
